/**
 * Minimal INI parser matching the configuration YKnytt uses:
 *   case-insensitive, duplicate keys override (last wins),
 *   duplicate sections merge, invalid lines skipped.
 * Section and key lookups are case-insensitive; values keep original case.
 */

export class IniSection {
  // key (lowercased) -> value
  private data = new Map<string, string>();
  // key (lowercased) -> original-case key (first seen)
  private names = new Map<string, string>();

  set(key: string, value: string): void {
    const lower = key.toLowerCase();
    this.data.set(lower, value);
    if (!this.names.has(lower)) this.names.set(lower, key);
  }

  get(key: string): string | null {
    const v = this.data.get(key.toLowerCase());
    return v === undefined ? null : v;
  }

  has(key: string): boolean {
    return this.data.has(key.toLowerCase());
  }

  keys(): string[] {
    return [...this.data.keys()];
  }

  /** The original-case spelling of a key (first occurrence wins). */
  originalKey(key: string): string {
    return this.names.get(key.toLowerCase()) ?? key;
  }
}

export class IniData {
  // section name (lowercased) -> section
  private sections = new Map<string, IniSection>();

  section(name: string): IniSection {
    const lower = name.toLowerCase();
    let s = this.sections.get(lower);
    if (!s) {
      s = new IniSection();
      this.sections.set(lower, s);
    }
    return s;
  }

  hasSection(name: string): boolean {
    return this.sections.has(name.toLowerCase());
  }

  /** Returns the section if present, else null (does not create). */
  getSection(name: string): IniSection | null {
    return this.sections.get(name.toLowerCase()) ?? null;
  }

  sectionNames(): string[] {
    return [...this.sections.keys()];
  }
}

export function parseIni(text: string): IniData {
  const data = new IniData();
  let current = data.section('');

  // Normalise line endings; the format uses CRLF.
  const lines = text.split(/\r\n|\r|\n/);
  for (let raw of lines) {
    const line = raw.trim();
    if (line.length === 0) continue;
    if (line.startsWith(';')) continue;

    if (line.startsWith('[')) {
      const end = line.indexOf(']');
      if (end < 0) continue; // invalid line, skip
      const name = line.substring(1, end);
      current = data.section(name);
      continue;
    }

    const eq = line.indexOf('=');
    if (eq < 0) continue; // invalid line, skip
    const key = line.substring(0, eq).trim();
    const value = line.substring(eq + 1).trim();
    if (key.length === 0) continue;
    current.set(key, value);
  }

  return data;
}
